/*!
 * Copyright (c) REWE Digital GmbH
 */
import { optimizeImageSrc } from "@rdss/core";
import { h } from "@stencil/core";
/**
 * VxPicture is a Stencil component that represents an optimized image.
 * It provides various properties to customize the image rendering, such as quality,
 * format, fit, gravity, and more. It also supports responsive design by allowing
 * different image sources for different viewport sizes.
 *
 * @example
 * <vx-picture
 *   src='image.jpg'
 *   alt='Alt text'
 *   height='300px'
 *   quality={90}
 *   decoding='auto'
 *   thumbnails={[
 *     {source: 'mobile.png', maxWidth: 600},
 *     {source: 'tablet.png', maxWidth: 1440},
 *   ]}
 *   format='avif'
 *   fit='scale-down'
 *   gravity='auto'
 *   alignVertical='center'
 *   alignHorizontal='center'
 * />
 *
 * @widgetName Pretty Picture
 * @widgetIcon https://tinyurl.com/yej4wzr6
 */
// eslint-disable-next-line require-jsdoc
export class VxPicture {
    constructor() {
        this.src = undefined;
        this.alt = undefined;
        this.height = undefined;
        this.width = undefined;
        this.quality = 80;
        this.decoding = 'auto';
        this.thumbnails = [];
        this.format = 'avif';
        this.loading = 'eager';
        this.fit = 'contain';
        this.gravity = 'auto';
        this.position = 'center';
        this.sharpen = 0;
        this.dpr = 1;
        this.maxDpr = 2;
        this.rotate = 0;
        this.innerThumbnails = [];
    }
    /**
     * Prepares the image options, alignment, source set, and sizes before the component loads.
     */
    componentWillLoad() {
        this.prepareThumbnails();
    }
    /**
     * Prepares the image options by merging the default options with the provided thumbnails.
     */
    prepareThumbnails() {
        var _a, _b;
        this.innerThumbnails = ((_a = this.thumbnails) === null || _a === void 0 ? void 0 : _a.length) === 0
            ? [Object.assign(Object.assign({}, this.prepareImage(this)), { src: this.getOptimizedImageSource(this) })]
            : (_b = this.thumbnails) === null || _b === void 0 ? void 0 : _b.map(thumb => this.prepareImage(thumb));
    }
    /**
     * @inheritdoc
     */
    render() {
        var _a, _b, _c;
        const fallbackImage = (_a = this.innerThumbnails) === null || _a === void 0 ? void 0 : _a.slice(-1)[0];
        return (h("picture", { key: '79cb6db7fee95ea082a094e03f10ee4d015a9334', part: 'picture' }, (_b = this.innerThumbnails) === null || _b === void 0 ? void 0 :
            _b.filter(thumb => thumb.maxWidth).map(thumbnail => h("source", { media: `(max-width: ${thumbnail.maxWidth}px)`, srcSet: this.getDprSourceSet(thumbnail), src: thumbnail.source })), h("img", { key: 'ff472a9814a724af8e70c525553db588acf155b8', part: 'image', alt: this.alt, src: (_c = fallbackImage === null || fallbackImage === void 0 ? void 0 : fallbackImage.source) !== null && _c !== void 0 ? _c : this.src, style: this.imageStyles, srcSet: this.getDprSourceSet(fallbackImage), loading: this.loading, decoding: this.decoding, "aria-label": this.alt })));
    }
    /**
     * Prepares the image by merging the default options with the provided image.
     */
    prepareImage(image) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
        return {
            maxWidth: image === null || image === void 0 ? void 0 : image.maxWidth,
            maxHeight: image.maxHeight,
            height: (_a = image.maxHeight) !== null && _a !== void 0 ? _a : this.height,
            width: (_b = image.maxWidth) !== null && _b !== void 0 ? _b : this.width,
            src: (_c = image.source) !== null && _c !== void 0 ? _c : this.src,
            dpr: (_d = image.dpr) !== null && _d !== void 0 ? _d : this.dpr,
            fit: (_e = image.fit) !== null && _e !== void 0 ? _e : this.fit,
            position: (_f = image.position) !== null && _f !== void 0 ? _f : this.position,
            gravity: (_g = image.gravity) !== null && _g !== void 0 ? _g : this.gravity,
            quality: (_h = image.quality) !== null && _h !== void 0 ? _h : this.quality,
            sharpen: (_j = image.sharpen) !== null && _j !== void 0 ? _j : this.sharpen,
            rotate: (_k = image.rotate) !== null && _k !== void 0 ? _k : this.rotate,
            format: (_l = image.format) !== null && _l !== void 0 ? _l : this.format,
        };
    }
    /**
     * Returns the styles for the image.
     */
    get imageStyles() {
        const { height = 'auto', width = '100%', fit, position } = this;
        const cropOrCover = ['cover', 'crop'].includes(this.fit);
        return {
            maxHeight: cropOrCover ? '100%' : String(isNaN(+height) ? height : height + 'px'),
            maxWidth: cropOrCover ? '100%' : String(isNaN(+width) ? width : width + 'px'),
            height: cropOrCover ? String(isNaN(+height) ? height : height + 'px') : '100%',
            width: cropOrCover ? String(isNaN(+width) ? width : width + 'px') : '100%',
            objectFit: fit === 'crop' ? 'cover' : fit,
            objectPosition: position,
        };
    }
    /**
     * Returns a source set string for the given size.
     * The source set string contains URLs for different device pixel ratios (DPRs), up to the maximum DPR.
     * Each URL is followed by its DPR value and the 'x' character.
     */
    getDprSourceSet(image) {
        return Array.from({ length: this.maxDpr }, (_, i) => i + 1)
            .map(dpr => `${this.getOptimizedImageSource(image, {
            dpr: this.dpr < this.maxDpr ? dpr : this.dpr,
        })} ${dpr}x`)
            .join(', ');
    }
    /**
     * Returns the optimized image source URL.
     * The URL is generated by the `optimizeImageSrc` function from the '@rdss/core' package.
     * The function takes the source URL and an options object, which can include various image options.
     */
    getOptimizedImageSource(image, imageOptions) {
        var _a;
        const options = Object.assign(Object.assign({}, image), imageOptions);
        const source = ((_a = options.src) === null || _a === void 0 ? void 0 : _a.length) > 0 ? options.src : this.src;
        if (!source)
            return;
        const originSource = this.normalizeImageUrl(source);
        const propertiesToRemove = ['file', 'position', 'src', 'maxHeight', 'maxWidth', 'retinaSource', 'source'];
        propertiesToRemove.forEach(property => delete options[property]);
        return optimizeImageSrc(originSource, options);
    }
    /**
     * Normalizes the image URL by removing the Cloudflare-specific parameters.
     */
    normalizeImageUrl(url) {
        return url.replace(/\/cdn-cgi\/image\/[^/]+/, '');
    }
    static get is() { return "vx-picture"; }
    static get encapsulation() { return "shadow"; }
    static get properties() {
        return {
            "src": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Bild"
                        }, {
                            "name": "widgetFieldType",
                            "text": "media"
                        }],
                    "text": "The URL of the image."
                },
                "attribute": "src",
                "reflect": false
            },
            "alt": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Bild Alt-Text"
                        }],
                    "text": "The alt text of the image."
                },
                "attribute": "alt",
                "reflect": false
            },
            "height": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | string",
                    "resolved": "number | string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "H\u00F6he des Bildes (px)"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "H\u00F6he des Bildes in px."
                        }],
                    "text": "The height of the image."
                },
                "attribute": "height",
                "reflect": false
            },
            "width": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | string",
                    "resolved": "number | string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Breite des Bildes (px)"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Breite des Bildes in px.\nWenn kein Wert angegeben ist, dann wird das Bild autom. an die Container-Breite angepasst."
                        }],
                    "text": "The width of the image."
                },
                "attribute": "width",
                "reflect": false
            },
            "quality": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "number | string",
                    "resolved": "number | string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Bild-Qualit\u00E4t"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Legt die Bild-Qualit\u00E4t fest (1 bis 100)"
                        }],
                    "text": "The quality of the image."
                },
                "attribute": "quality",
                "reflect": false,
                "defaultValue": "80"
            },
            "decoding": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'auto' | 'sync' | 'async'",
                    "resolved": "\"async\" | \"auto\" | \"sync\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Decoding"
                        }, {
                            "name": "widgetFieldType",
                            "text": "selectBox"
                        }, {
                            "name": "widgetFieldStore",
                            "text": "{{\n'automatisch': 'auto',\n'asynchron': 'async',\n'synchron': 'sync'\n}}"
                        }],
                    "text": "The decoding method of the image."
                },
                "attribute": "decoding",
                "reflect": false,
                "defaultValue": "'auto'"
            },
            "thumbnails": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "Image[]",
                    "resolved": "Image[]",
                    "references": {
                        "Image": {
                            "location": "global",
                            "id": "global::Image"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldIgnore",
                            "text": undefined
                        }],
                    "text": "The thumbnails of the image."
                },
                "defaultValue": "[]"
            },
            "format": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "CloudflareImageOptions['format'] | string",
                    "resolved": "string",
                    "references": {
                        "CloudflareImageOptions": {
                            "location": "import",
                            "path": "@rdss/core",
                            "id": "../core/dist/internal/index.d.ts::CloudflareImageOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The format of the image."
                },
                "attribute": "format",
                "reflect": false,
                "defaultValue": "'avif'"
            },
            "loading": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "'eager' | 'lazy'",
                    "resolved": "\"eager\" | \"lazy\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Loading"
                        }, {
                            "name": "widgetFieldType",
                            "text": "selectBox"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Legt fest ob das Bild 'faul' oder direkt geladen wird.\nNutze 'lazy', falls das Bild nicht direkt im Viewport sichtbar ist, sondern erst beim Scrollen."
                        }, {
                            "name": "widgetFieldStore",
                            "text": "{{\n'eager': 'eager',\n'lazy': 'lazy'\n}}"
                        }],
                    "text": "The format of the image."
                },
                "attribute": "loading",
                "reflect": false,
                "defaultValue": "'eager'"
            },
            "fit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "CloudflareImageOptions['fit'] | string",
                    "resolved": "string",
                    "references": {
                        "CloudflareImageOptions": {
                            "location": "import",
                            "path": "@rdss/core",
                            "id": "../core/dist/internal/index.d.ts::CloudflareImageOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Bildanpassung (Fit)"
                        }, {
                            "name": "widgetFieldType",
                            "text": "selectBox"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "'verkleinern': Passt Bild an Platz an, ohne zu vergr\u00F6\u00DFern.\n'einpassen': Bild passt in den Platz, Seitenverh\u00E4ltnis. bleibt gleich.\n'zuschneiden': Schneidet Bild auf Platz zu, Teile k\u00F6nnen wegfallen.\n'ausf\u00FCllen': F\u00FCllt Platz aus, Teile k\u00F6nnen wegfallen."
                        }, {
                            "name": "widgetFieldStore",
                            "text": "{{\n'scale-down': 'verkleinern',\n'contain': 'einpassen',\n'crop': 'zuschneiden',\n'cover': 'ausf\u00FCllen'\n}}"
                        }],
                    "text": "The fit method of the image.\n\nAffects interpretation of width and height. All resizing modes preserve aspect ratio."
                },
                "attribute": "fit",
                "reflect": false,
                "defaultValue": "'contain'"
            },
            "gravity": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "CloudflareImageOptions['gravity'] | string",
                    "resolved": "string | { x: number; y: number; }",
                    "references": {
                        "CloudflareImageOptions": {
                            "location": "import",
                            "path": "@rdss/core",
                            "id": "../core/dist/internal/index.d.ts::CloudflareImageOptions"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldIgnore",
                            "text": undefined
                        }],
                    "text": "The gravity of the image.\n\nWhen cropping with fit: 'cover' and fit: 'crop', this parameter defines the side\nor point that should not be cropped."
                },
                "attribute": "gravity",
                "reflect": false,
                "defaultValue": "'auto'"
            },
            "position": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ImagePosition",
                    "resolved": "\"bottom\" | \"center bottom\" | \"center center\" | \"center top\" | \"center\" | \"left bottom\" | \"left center\" | \"left top\" | \"left\" | \"right bottom\" | \"right center\" | \"right top\" | \"right\" | \"top\"",
                    "references": {
                        "ImagePosition": {
                            "location": "global",
                            "id": "global::ImagePosition"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Position"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Legt die Position des Bildes fest."
                        }, {
                            "name": "widgetFieldType",
                            "text": "selectBox"
                        }, {
                            "name": "widgetFieldStore",
                            "text": "{{\n'center': 'Zentriert',\n'right': 'Rechts',\n'left': 'Links',\n'top': 'Oben',\n'bottom': 'Unten',\n'left top': 'Links oben',\n'left center': 'Links zentriert',\n'left bottom': 'Links unten',\n'center top': 'Mitte oben',\n'center center': 'Mitte zentriert',\n'center bottom': 'Mitte unten',\n'right top': 'Rechts oben',\n'right center': 'Rechts zentriert',\n'right bottom': 'Rechts unten'\n}}"
                        }],
                    "text": "The position of the image."
                },
                "attribute": "position",
                "reflect": false,
                "defaultValue": "'center'"
            },
            "sharpen": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Sch\u00E4rfe"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Sch\u00E4rfe des Bildes"
                        }],
                    "text": "Specifies strength of sharpening filter to apply to the image."
                },
                "attribute": "sharpen",
                "reflect": false,
                "defaultValue": "0"
            },
            "dpr": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "DPR Bild (Device Pixel Ratio)"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Ger\u00E4te-Pixelverh\u00E4ltnis. Bei detail-reichen Bildern wird eine dpr von 2 empfohlen."
                        }],
                    "text": "Device Pixel Ratio. Default is 1. Multiplier for width/height that makes\nit easier to specify higher-DPI sizes in <img srcset>."
                },
                "attribute": "dpr",
                "reflect": false,
                "defaultValue": "1"
            },
            "maxDpr": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Maximales DPR"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Maximales DPR, welches unterst\u00FCtzt wird."
                        }],
                    "text": "The maximum Device Pixel Ratio (DPR) for the image.\nThis prop is used to generate the source set for the image."
                },
                "attribute": "max-dpr",
                "reflect": false,
                "defaultValue": "2"
            },
            "rotate": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [{
                            "name": "widgetFieldLabel",
                            "text": "Rotieren"
                        }, {
                            "name": "widgetFieldSupportText",
                            "text": "Die Anzahl um wieviel Grad (90, 180 oder 270) das Bild gedreht wird.\nDie Optionen f\u00FCr Breite und H\u00F6he beziehen sich auf die Achsen nach der Drehung."
                        }],
                    "text": "Number of degrees (90, 180, or 270) to rotate the image by width and height options\nrefer to axes after rotation."
                },
                "attribute": "rotate",
                "reflect": false,
                "defaultValue": "0"
            }
        };
    }
    static get states() {
        return {
            "innerThumbnails": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "src",
                "methodName": "prepareThumbnails"
            }, {
                "propName": "thumbnails",
                "methodName": "prepareThumbnails"
            }];
    }
}
//# sourceMappingURL=vx-picture.js.map
